import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs, processGrandFinalResult } from "../../api/examApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");

/**
 * Direct port of digicampusbd's `exam-result-process-grand-final` ("Exam
 * Result Process — Grand Final") — combines a class's already-processed
 * component exams (any exam for that class with a real `percentage` weight
 * configured, e.g. "1st semester 33%, 2nd semester 34%, 3rd semester 33%")
 * into one combined result under the class's own "Grand Final" exam, per
 * `GrandFinalResultProcessService`'s 4 distinct algorithms selected by that
 * exam's own `grandFinalProcessType` (1 = combine at the mark/subject level;
 * 2/3/4 = combine already-processed exam results — weighted-average,
 * weighted-average-but-must-pass-every-component, and simple average,
 * respectively). All 4 ported faithfully server-side — see
 * `GrandFinalResultService`'s own doc comment for the full breakdown.
 *
 * Only a class's real "Grand Final" exam (`examDefaultId === 99`) is
 * selectable here, matching the old app's own restriction. For Process
 * Type 1, this only combines per-subject marks into new `ExamMark` rows —
 * running the already-existing Exam Result Process (General) page again
 * against this same Grand Final exam is a separate, subsequent step to turn
 * those into class positions, exactly like the old app's own two-step flow.
 */
export default function GrandFinalResultProcessPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
  }, []);

  useEffect(() => {
    if (!classConfigId) return;
    const config = classConfigs.find((c) => String(c.id) === String(classConfigId));
    if (!config) return;
    listExamConfigs(config.classId).then((list) => {
      const grandFinalOnly = list.filter((ec) => ec.examDefaultId === 99);
      setExamConfigs(grandFinalOnly);
      setExamConfigId(grandFinalOnly.length > 0 ? String(grandFinalOnly[0].examConfigId) : "");
    });
  }, [classConfigId, classConfigs]);

  const handleProcess = async () => {
    setError("");
    setResult(null);
    if (!examConfigId) {
      setError("This class has no Grand Final exam configured yet.");
      return;
    }
    setProcessing(true);
    try {
      const res = await processGrandFinalResult(examConfigId, academicYear);
      setResult(res.message);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to process Grand Final result.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Exam Result Process — Grand Final</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      {result && <p style={{ color: "var(--color-success, green)" }}>{result}</p>}
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Grand Final Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No Grand Final exam configured for this class</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleProcess} disabled={processing || !examConfigId}>
          {processing ? "Processing..." : "Process"}
        </button>
      </div>
      <p>
        Combines this class's already-processed component exams (any exam with a real percentage weight configured) into one
        combined Grand Final result, using this exam's own configured process type.
      </p>
    </MainLayout>
  );
}
